import React from "react";
import { Link, LinkProps } from "react-router-dom";

/**
 * True for an ordinary left click with no modifier keys - the click a mouse user makes on
 * a card to select it or start dragging it. Keyboard activation (Enter on the link)
 * arrives as a click with `detail === 0`, so it is not one of these.
 */
export function isPlainMouseClick(e: Pick<React.MouseEvent, "detail" | "button" | "ctrlKey" | "metaKey" | "shiftKey" | "altKey">): boolean {
    return e.detail > 0 && e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey;
}

export type CardLinkProps = Omit<LinkProps, "onClick"> & {
    /** Called when the link is followed from the keyboard, before navigation. */
    onKeyboardOpen?: () => void;
};

/**
 * The name on a card, as a real link, for cards whose own mouse handling (select on
 * click, open on double click, drag) must stay as it is. It makes the card reachable with
 * Tab and openable with Enter, and keeps middle-click and ctrl-click "open in new tab",
 * but a plain mouse click is left to the card - the link does not navigate on it.
 *
 * A keyboard open stops at the link, so a card-level click handler (which would toggle
 * the selection) does not see it; `onKeyboardOpen` is where to select instead.
 */
export function CardLink(props: CardLinkProps) {

    const { onKeyboardOpen, className, style, ...rest } = props;

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (isPlainMouseClick(e)) {
            e.preventDefault();
            return;
        }
        if (e.detail === 0) {
            e.stopPropagation();
            onKeyboardOpen?.();
        }
    };

    // The card's own cursor (grab, pointer) stays over its name too.
    return <Link draggable={false} {...rest} style={{ cursor: "inherit", ...style }}
        className={`link-plain ${className ?? ""}`} onClick={handleClick} />;
}

export default CardLink;
